import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SpecialOffersSection from "../components/SpecialOffers";
import { useCurrency } from "../lib/currency";
import { ArrowRight } from "lucide-react";

export default function Store() {
  const [apex, setApex] = useState([]);
  const { format, code, isBase } = useCurrency();

  useEffect(() => {
    api.get("/apex-products").then(r => setApex(r.data));
    // Support Event Board deep-link `/store#special-offers` — RRD skips
    // fragment scrolling by default so we do it after render + fetch.
    if (window.location.hash === "#special-offers") {
      setTimeout(() => {
        const el = document.getElementById("special-offers");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  }, []);

  return (
    <div>
      <Header/>
      <div className="max-w-7xl mx-auto pt-32 pb-16 px-4 sm:px-8">
        <div className="mono-caption">Age of Empires Mobile</div>
        <h1 className="font-serif text-4xl lg:text-5xl font-bold mt-2 mb-4"><span className="gold-text">Apex Store</span></h1>
        {!isBase && <div className="mb-4 text-sm text-[var(--av-text-dim)]">Prices displayed in <span className="text-[var(--av-gold)]">{code}</span> — all orders are securely charged in <span className="text-[var(--av-gold)]">GBP</span>.</div>}

        {/* Daily / Weekly / Monthly offers — sits ABOVE the standard packs
            grid. Deliberately a separate section so normal Apex products
            and their pricing tiers remain untouched. */}
        <div className="mb-10">
          <SpecialOffersSection/>
        </div>

        <p className="text-[var(--av-text-dim)] mb-6 text-sm">Pick any denomination, then choose <span className="text-[var(--av-gold)]">1–100 packs</span> (up to <span className="text-[var(--av-gold)]">200</span> on the 499 Apex pack). The more you buy, the better the price per pack.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="apex-grid">
          {apex.map(p => {
            const cheapest = Math.min(...p.tiers.map(t => t.unit_price_gbp));
            const startPrice = p.tiers[0]?.unit_price_gbp || 0;
            const isBestValue = p.apex_amount === 499;
            const isOwnersPick = p.apex_amount === 999;
            const isFeatured = isBestValue || isOwnersPick;
            const cardCls = isBestValue
              ? "vault-card p-6 block relative overflow-hidden ring-2 ring-[var(--av-gold)] shadow-[0_0_30px_-8px_rgba(212,175,55,0.55)]"
              : isOwnersPick
                ? "vault-card p-6 block relative overflow-hidden ring-2 ring-[var(--av-purple)] shadow-[0_0_28px_-10px_rgba(148,0,211,0.55)]"
                : "vault-card p-6 block relative overflow-hidden";
            return (
              <Link to={`/apex/${p.id}`} key={p.id} data-testid={`apex-card-${p.apex_amount}`} className={cardCls}>
                {isFeatured && (
                  <div
                    data-testid={`ribbon-${p.apex_amount}`}
                    className={`absolute top-3 -right-8 rotate-45 w-32 text-center py-1 text-[10px] font-serif font-bold tracking-wider uppercase shadow-lg ${
                      isBestValue
                        ? "bg-gradient-to-r from-[#d4af37] to-[#a17c1a] text-[#0b0c10]"
                        : "bg-gradient-to-r from-[#9400D3] to-[#5b1c8a] text-white"
                    }`}
                  >
                    {isBestValue ? "★ Best Value" : "★ Owner's Pick"}
                  </div>
                )}
                <div className="mono-caption mb-2">
                  {isFeatured && (
                    <span className={`inline-block mr-2 pill ${isBestValue ? "pill-gold" : "pill-purple"}`}>
                      {isBestValue ? "Cheapest per Apex" : "Owner favourite"}
                    </span>
                  )}
                  Apex Denomination
                </div>
                <div className="font-serif text-3xl font-bold gold-text mb-1">{p.apex_amount.toLocaleString()} Apex</div>
                <div className="text-sm text-[var(--av-text-dim)] mb-4">per pack</div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-2xl font-bold">{format(startPrice)}</span>
                  {p.rrp_unit_gbp && p.rrp_unit_gbp > startPrice && (
                    <span className="text-sm text-[var(--av-text-muted)] line-through decoration-[var(--av-purple)]/70">{format(p.rrp_unit_gbp)}</span>
                  )}
                </div>
                <div className="text-[11px] text-[var(--av-text-dim)] mb-3">from · {format(cheapest)} at 100</div>
                {p.rrp_unit_gbp && p.rrp_unit_gbp > cheapest && (
                  <div className="text-[11px] purple-text font-semibold mb-2">Save up to {Math.round((1 - cheapest / p.rrp_unit_gbp) * 100)}%</div>
                )}
                <div className="pill pill-purple mb-4">1–100 pack bulk pricing</div>
                <div className="text-[var(--av-gold)] text-sm flex items-center justify-between mt-2">
                  Configure quantity <ArrowRight className="w-4 h-4"/>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
      <Footer/>
    </div>
  );
}
